import { useState } from 'react'
import {
  Building2,
  Users,
  RefreshCw,
  AlertCircle,
  ChevronDown,
  ChevronRight,
  Mail,
  Trash2,
} from 'lucide-react'
import { cn } from '@/lib/utils'
import {
  useWorkspaces,
  useWorkspaceMembers,
  useInviteMember,
  useUpdateMemberRole,
  useRemoveMember,
} from '@/hooks/useWorkspaces'
import type { WorkspaceRole } from '@/types/api'

// ─── ユーティリティ ───

function formatBytes(bytes: number): string {
  if (bytes < 1024) return `${bytes} B`
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`
  if (bytes < 1024 * 1024 * 1024) return `${(bytes / 1024 / 1024).toFixed(1)} MB`
  return `${(bytes / 1024 / 1024 / 1024).toFixed(2)} GB`
}

function formatDate(iso: string): string {
  const d = new Date(iso)
  return `${d.getFullYear()}/${d.getMonth() + 1}/${d.getDate()}`
}

const ROLE_LABELS: Record<WorkspaceRole, string> = {
  owner: 'オーナー',
  admin: '管理者',
  member: 'メンバー',
  viewer: '閲覧者',
}

const STATUS_COLORS = {
  active: 'bg-green-100 text-green-700',
  suspended: 'bg-red-100 text-red-700',
  trial: 'bg-yellow-100 text-yellow-700',
}

const STATUS_LABELS = {
  active: '稼働中',
  suspended: '停止中',
  trial: 'トライアル',
}

// ─── メンバー一覧パネル ───

function MembersPanel({ workspaceId }: { workspaceId: string }) {
  const [inviteLineUserId, setInviteLineUserId] = useState('')
  const [inviteDisplayName, setInviteDisplayName] = useState('')
  const [inviteRole, setInviteRole] = useState<'admin' | 'member'>('member')
  const [inviteError, setInviteError] = useState<string | null>(null)

  const { data, isLoading, error } = useWorkspaceMembers(workspaceId)
  const inviteMutation = useInviteMember(workspaceId)
  const updateRoleMutation = useUpdateMemberRole(workspaceId)
  const removeMutation = useRemoveMember(workspaceId)

  const handleInvite = async () => {
    if (!inviteLineUserId.trim()) {
      setInviteError('ユーザーID（Clerk sub）を入力してください')
      return
    }
    setInviteError(null)
    try {
      await inviteMutation.mutateAsync({
        lineUserId: inviteLineUserId.trim(),
        displayName: inviteDisplayName.trim() || undefined,
        role: inviteRole,
      })
      setInviteLineUserId('')
      setInviteDisplayName('')
    } catch {
      setInviteError('メンバー追加に失敗しました')
    }
  }

  if (isLoading) {
    return <div className="h-20 animate-pulse rounded bg-muted" />
  }

  if (error) {
    return (
      <div className="flex items-center gap-2 text-sm text-red-600">
        <AlertCircle className="h-4 w-4" />
        メンバー取得に失敗しました
      </div>
    )
  }

  return (
    <div className="space-y-4">
      {/* メンバー追加フォーム */}
      <div className="flex gap-2">
        <input
          type="text"
          value={inviteLineUserId}
          onChange={(e) => setInviteLineUserId(e.target.value)}
          placeholder="ユーザーID（Clerk sub）"
          className="flex-1 rounded-md border bg-background px-3 py-1.5 text-sm"
        />
        <input
          type="text"
          value={inviteDisplayName}
          onChange={(e) => setInviteDisplayName(e.target.value)}
          placeholder="表示名（任意）"
          className="flex-1 rounded-md border bg-background px-3 py-1.5 text-sm"
        />
        <select
          value={inviteRole}
          onChange={(e) => setInviteRole(e.target.value as 'admin' | 'member')}
          className="rounded-md border bg-background px-3 py-1.5 text-sm"
        >
          {(['admin', 'member'] as const).map((r) => (
            <option key={r} value={r}>{ROLE_LABELS[r]}</option>
          ))}
        </select>
        <button
          onClick={() => void handleInvite()}
          disabled={inviteMutation.isPending}
          className="flex items-center gap-1 rounded-md bg-primary px-3 py-1.5 text-sm text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
        >
          <Mail className="h-3.5 w-3.5" />
          追加
        </button>
      </div>
      {inviteError && (
        <p className="text-xs text-red-600">{inviteError}</p>
      )}

      {/* メンバー一覧 */}
      <div className="divide-y rounded-md border">
        {data?.members.map((m) => (
          <div key={m.id} className="flex items-center justify-between px-4 py-2">
            <div>
              <p className="text-sm font-medium">{m.displayName}</p>
              <p className="text-xs text-muted-foreground">{m.email}</p>
            </div>
            <div className="flex items-center gap-2">
              <select
                value={m.role}
                disabled={m.role === 'owner' || updateRoleMutation.isPending}
                onChange={(e) =>
                  void updateRoleMutation.mutateAsync({
                    memberId: m.id,
                    role: e.target.value as WorkspaceRole,
                  })
                }
                className="rounded border bg-background px-2 py-1 text-xs disabled:opacity-50"
              >
                {(Object.keys(ROLE_LABELS) as WorkspaceRole[]).map((r) => (
                  <option key={r} value={r}>{ROLE_LABELS[r]}</option>
                ))}
              </select>
              {m.role !== 'owner' && (
                <button
                  onClick={() => void removeMutation.mutateAsync(m.id)}
                  disabled={removeMutation.isPending}
                  className="rounded p-1 text-muted-foreground hover:text-red-600 disabled:opacity-50"
                  title="メンバーを削除"
                >
                  <Trash2 className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          </div>
        ))}
        {data?.members.length === 0 && (
          <div className="px-4 py-4 text-center text-sm text-muted-foreground">
            メンバーがいません
          </div>
        )}
      </div>
    </div>
  )
}

// ─── メインページ ───

export function WorkspacesPage() {
  const [expandedId, setExpandedId] = useState<string | null>(null)

  const { data, isLoading, error, refetch } = useWorkspaces()

  const toggle = (id: string) => {
    setExpandedId((prev) => (prev === id ? null : id))
  }

  return (
    <div className="space-y-6">
      {/* ヘッダー */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">ワークスペース管理</h1>
          <p className="text-muted-foreground">全ワークスペースとメンバーを管理します</p>
        </div>
        <button
          onClick={() => void refetch()}
          disabled={isLoading}
          className="flex items-center gap-2 rounded-lg border px-4 py-2 text-sm hover:bg-muted disabled:opacity-50"
        >
          <RefreshCw className={cn('h-4 w-4', isLoading && 'animate-spin')} />
          更新
        </button>
      </div>

      {/* エラー */}
      {error && (
        <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 p-4 text-red-700">
          <AlertCircle className="h-5 w-5" />
          <span className="text-sm">ワークスペース一覧の取得に失敗しました</span>
        </div>
      )}

      {/* ワークスペース一覧 */}
      {isLoading ? (
        <div className="space-y-3">
          {[...Array(3)].map((_, i) => (
            <div key={i} className="h-20 animate-pulse rounded-lg border bg-card" />
          ))}
        </div>
      ) : (
        <div className="space-y-2">
          {data?.workspaces.map((ws) => (
            <div key={ws.id} className="rounded-lg border bg-card shadow-sm">
              {/* ワークスペース行 */}
              <button
                onClick={() => toggle(ws.id)}
                className="flex w-full items-center gap-4 px-5 py-4 text-left hover:bg-muted/40"
              >
                <Building2 className="h-5 w-5 shrink-0 text-muted-foreground" />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-medium">{ws.name}</span>
                    <span className="text-xs text-muted-foreground">({ws.id})</span>
                    <span className={cn('rounded-full px-2 py-0.5 text-xs font-medium', STATUS_COLORS[ws.status])}>
                      {STATUS_LABELS[ws.status]}
                    </span>
                    <span className="rounded-full border px-2 py-0.5 text-xs text-muted-foreground">
                      {ws.plan}
                    </span>
                  </div>
                  <div className="mt-0.5 flex items-center gap-4 text-xs text-muted-foreground">
                    <span className="flex items-center gap-1">
                      <Users className="h-3 w-3" />
                      {ws.memberCount} 名
                    </span>
                    <span>ストレージ: {formatBytes(ws.storageUsedBytes)}</span>
                    <span>作成: {formatDate(ws.createdAt)}</span>
                  </div>
                </div>
                {expandedId === ws.id ? (
                  <ChevronDown className="h-4 w-4 text-muted-foreground" />
                ) : (
                  <ChevronRight className="h-4 w-4 text-muted-foreground" />
                )}
              </button>

              {/* メンバーパネル */}
              {expandedId === ws.id && (
                <div className="border-t px-5 py-4">
                  <h3 className="mb-3 text-sm font-semibold">メンバー管理</h3>
                  <MembersPanel workspaceId={ws.id} />
                </div>
              )}
            </div>
          ))}

          {data?.workspaces.length === 0 && (
            <div className="rounded-lg border bg-card p-10 text-center text-muted-foreground">
              ワークスペースがありません
            </div>
          )}
        </div>
      )}
    </div>
  )
}
